// src/components/about/AboutUs.jsx
import React, { useState, useEffect, useRef } from "react";

import { Container, Row, Col } from "react-bootstrap";
import { FaCheckCircle, FaLeaf, FaTractor, FaHandshake } from "react-icons/fa";

import Button from "../../../../components/general/button/Button";
import { AboutUsSkeleton } from "../../../../components/general/skeleton/SkeletonBlock";
import "../../../../assets/css/customer/home/aboutus/AboutUs.css";
import { useNavigate } from "react-router-dom";
import wheat from "../../../../assets/images/home/wheat.jpg";
import greenLeaves from "../../../../assets/images/home/aboutUs/greenLeaves.jpeg";

const AboutUs = () => {
  const [isContentVisible, setIsContentVisible] = useState(false);
  const [isImagesVisible, setIsImagesVisible] = useState(false);
  const [isStatsVisible, setIsStatsVisible] = useState(false);
  const [loading, setLoading] = useState(true);

  const contentRef = useRef(null);
  const imagesRef = useRef(null);
  const statsRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 1100);
    return () => clearTimeout(t);
  }, []);

  const navigate = useNavigate();

  const handleLearnMore = () => {
    navigate("/about");
  };

  // Platform information instead of fake user/product numbers
  const stats = [
    {
      value: "4",
      label: "Platform Roles",
    },
    {
      value: "1",
      label: "Direct Marketplace",
    },
    {
      value: "24/7",
      label: "Online Access",
    },
  ];

  const features = [
    {
      icon: <FaLeaf />,
      title: "Agricultural Marketplace",
      description:
        "Buy seeds, fertilizers, pesticides, and agricultural products through one digital platform.",
    },
    {
      icon: <FaTractor />,
      title: "Digital Agriculture",
      description:
        "Helping farmers and store owners manage products, orders, and customers through digital tools.",
    },
    {
      icon: <FaHandshake />,
      title: "Direct Marketplace",
      description:
        "Connect farmers, store owners, and customers directly without traditional middleman involvement.",
    },
  ];

  // Content Section Observer
  useEffect(() => {
    if (loading) return;

    const contentObserver = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setIsContentVisible(true);
        contentObserver.disconnect();
      }
    });

    if (contentRef.current) {
      contentObserver.observe(contentRef.current);
    }

    return () => contentObserver.disconnect();
  }, [loading]);

  // Images Section Observer
  useEffect(() => {
    if (loading) return;

    const imagesObserver = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setIsImagesVisible(true);
        imagesObserver.disconnect();
      }
    });

    if (imagesRef.current) {
      imagesObserver.observe(imagesRef.current);
    }

    return () => imagesObserver.disconnect();
  }, [loading]);

  // Stats Section Observer
  useEffect(() => {
    if (loading) return;

    const statsObserver = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setIsStatsVisible(true);
        statsObserver.disconnect();
      }
    });

    if (statsRef.current) {
      statsObserver.observe(statsRef.current);
    }

    return () => statsObserver.disconnect();
  }, [loading]);

  // Show sections after loading
  useEffect(() => {
    if (!loading) {
      const timer = setTimeout(() => {
        setIsContentVisible(true);
        setIsImagesVisible(true);
        setIsStatsVisible(true);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [loading]);

  return (
    <>
      {loading ? (
        <AboutUsSkeleton />
      ) : (
        <section className="about-us-wrapper">
          <Container>
            <Row className="align-items-center">
              {/* LEFT SIDE - TEXT CONTENT */}
              <Col xs={12} lg={6} className="mb-5 mb-lg-0">
                <div
                  ref={contentRef}
                  className={`about-us-content ${
                    isContentVisible ? "content-visible" : ""
                  }`}
                >
                  {/* Badge */}
                  {/* <div className="about-us-badge">
                    <FaLeaf className="about-badge-icon" />
                    <span className="about-badge-text">ABOUT KISAANMART</span>
                  </div> */}

                  {/* Title */}
                  <h2 className="about-us-title">
                    Empowering Farmers,
                    <br />
                    Connecting Customers
                  </h2>

                  {/* Description */}
                  <p className="about-us-description about-description-1">
                    KisaanMart is a digital agricultural marketplace designed to
                    connect farmers, store owners, customers, and deliverymen on
                    one platform. Farmers can sell their agricultural products
                    directly, while customers can easily find and purchase the
                    products they need.
                  </p>

                  <p className="about-us-description about-description-2">
                    The platform also supports agricultural inputs such as
                    seeds, fertilizers, and pesticides, along with secure
                    ordering, payment, delivery, and customer support features.
                    KisaanMart aims to make agricultural trading simpler, more
                    accessible, and digitally connected.
                  </p>

                  {/* FEATURES LIST */}
                  <div className="about-features-list">
                    {features.map((feature, index) => (
                      <div
                        key={index}
                        className="about-feature-item"
                        style={{
                          "--feature-delay": `${index * 0.2}s`,
                        }}
                      >
                        <div className="about-feature-icon">{feature.icon}</div>

                        <div className="about-feature-content">
                          <h5 className="about-feature-title">
                            {feature.title}
                          </h5>

                          <p className="about-feature-text">
                            {feature.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* CTA Section */}
                  <div className="about-cta-section">
                    <Button
                      variant="gradient"
                      size="medium"
                      onClick={handleLearnMore}
                    >
                      Learn More
                    </Button>
                  </div>
                </div>
              </Col>

              {/* RIGHT SIDE - IMAGES */}
              <Col xs={12} lg={6}>
                <div className="about-us-images">
                  <div
                    ref={imagesRef}
                    className={`about-images-wrapper ${
                      isImagesVisible ? "images-visible" : ""
                    }`}
                  >
                    {/* MAIN IMAGE */}
                    <div className="about-main-image">
                      <img
                        src={wheat}
                        alt="Pakistani Farmer with Fresh Crops"
                      />

                      <div className="about-image-overlay">
                        <FaCheckCircle className="about-overlay-icon" />
                        <span className="about-overlay-text">
                          Verified Quality
                        </span>
                      </div>
                    </div>

                    {/* SECONDARY IMAGE */}
                    <div className="about-secondary-image">
                      <img src={greenLeaves} alt="Agricultural Products" />
                    </div>

                    {/* DECORATIVE ELEMENTS */}
                    <div className="about-green-circle"></div>
                    <div className="about-dots-pattern"></div>
                  </div>

                  {/* PLATFORM STATS */}
                  <Row
                    ref={statsRef}
                    className={`about-stats-grid g-3 ${
                      isStatsVisible ? "stats-visible" : ""
                    }`}
                  >
                    {stats.map((stat, index) => (
                      <Col
                        key={index}
                        xs={6}
                        sm={4}
                        lg={4}
                        style={{
                          "--stat-delay": `${index * 0.15}s`,
                        }}
                      >
                        <div className="about-stat-card">
                          <h3 className="about-stat-number">{stat.value}</h3>

                          <p className="about-stat-label">{stat.label}</p>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </div>
              </Col>
            </Row>
          </Container>
        </section>
      )}
    </>
  );
};

export default AboutUs;
